import { useRef, useState } from "react";
import { Search, Upload, Eye, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { errorMessage } from "@/lib/error-message";
import { QueryError } from "@/components/Common/QueryError";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { useMediaList, useUploadMedia, useDeleteMedia } from "../../features/media/queries";
import { showDeleteConfirm } from "../Common/DeleteConfirm";
import { cn } from "@/lib/utils";

interface MediaPickerProps {
  open: boolean;
  onClose: () => void;
  onSelect: (url: string) => void;
  multiple?: boolean;
  // Açan formun kategorisi: hem filtre hem yükleme varsayılanı olur.
  defaultFolder?: "brands" | "blog" | "content" | "social" | "general";
}

const TYPE_FILTERS = [
  { label: "Tümü", value: "__all__" },
  { label: "Resimler", value: "image/" },
  { label: "Videolar", value: "video/" },
  { label: "Dokümanlar", value: "application/" },
] as const;

const FOLDERS = [
  { label: "Markalar", value: "brands" },
  { label: "Blog", value: "blog" },
  { label: "İçerik", value: "content" },
  { label: "Sosyal", value: "social" },
  { label: "Genel", value: "general" },
] as const;

// Radix Select requires non-empty string values; we use "__all__" sentinel for "undefined"
const SENTINEL_ALL = "__all__";

export default function MediaPicker({
  open,
  onClose,
  onSelect,
  multiple = false,
  defaultFolder,
}: MediaPickerProps) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedUrl, setSelectedUrl] = useState<string | null>(null);
  const [mimeTypeFilter, setMimeTypeFilter] = useState<string>(SENTINEL_ALL);
  const [folderFilter, setFolderFilter] = useState<string>(defaultFolder ?? SENTINEL_ALL);
  const uploadFolder = defaultFolder ?? "general";

  const fileInputRef = useRef<HTMLInputElement>(null);

  const activeMime = mimeTypeFilter === SENTINEL_ALL ? undefined : mimeTypeFilter;
  const activeFolder = folderFilter === SENTINEL_ALL ? undefined : folderFilter;

  const {
    data: mediaData,
    isLoading,
    isError,
    error,
    refetch,
  } = useMediaList({
    page,
    limit: 24,
    search: search || undefined,
    mimeType: activeMime,
    folder: activeFolder,
  });
  const uploadMutation = useUploadMedia();
  const deleteMutation = useDeleteMedia();

  const handleUpload = async (file: File) => {
    try {
      await uploadMutation.mutateAsync({ file, folder: uploadFolder });
      toast.success("Dosya yüklendi");
    } catch (err) {
      toast.error(errorMessage(err, "Yükleme başarısız"));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleUpload(file);
      // Reset input so the same file can be re-uploaded if needed
      e.target.value = "";
    }
  };

  const handleSelect = () => {
    if (selectedUrl) {
      onSelect(selectedUrl);
      setSelectedId(null);
      setSelectedUrl(null);
    }
  };

  const handleClose = () => {
    setSelectedId(null);
    setSelectedUrl(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) handleClose();
      }}
    >
      <DialogContent className="max-w-4xl w-full">
        <DialogHeader>
          <DialogTitle>Medya Kütüphanesi</DialogTitle>
        </DialogHeader>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Ara..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="pl-8 w-48"
              />
            </div>

            <Select
              value={mimeTypeFilter}
              onValueChange={(v) => {
                setMimeTypeFilter(v);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TYPE_FILTERS.map((f) => (
                  <SelectItem key={f.value} value={f.value}>
                    {f.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={folderFilter}
              onValueChange={(v) => {
                setFolderFilter(v);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-36" title="Kategori">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={SENTINEL_ALL}>Tüm kategoriler</SelectItem>
                {FOLDERS.map((f) => (
                  <SelectItem key={f.value} value={f.value}>
                    {f.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            multiple={multiple}
            onChange={handleFileChange}
          />
          <Button onClick={() => fileInputRef.current?.click()} disabled={uploadMutation.isPending}>
            {uploadMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Upload className="h-4 w-4" />
            )}
            Dosya Yükle
          </Button>
        </div>

        {/* Grid */}
        <div className="min-h-[300px]">
          {isLoading ? (
            <div className="flex items-center justify-center h-48">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : isError && !mediaData ? (
            <QueryError error={error} onRetry={() => void refetch()} />
          ) : !mediaData?.items?.length ? (
            <div className="flex flex-col items-center justify-center h-48 text-muted-foreground text-sm">
              Henüz medya dosyası yok
            </div>
          ) : (
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-3">
              {mediaData.items.map((item) => (
                <div
                  key={item.id}
                  onClick={() => {
                    setSelectedId(item.id);
                    setSelectedUrl(item.url);
                  }}
                  className={cn(
                    "group relative rounded-md border-2 cursor-pointer overflow-hidden bg-muted/30 transition-colors hover:border-primary",
                    selectedId === item.id ? "border-primary" : "border-transparent",
                  )}
                >
                  {/* Thumbnail */}
                  <div className="h-20 flex items-center justify-center bg-muted/20">
                    {item.mimeType?.startsWith("image/") ? (
                      <img
                        src={item.url}
                        alt={item.alt || item.originalName}
                        className="max-w-full max-h-20 object-contain"
                      />
                    ) : (
                      <span className="text-xs text-muted-foreground font-medium uppercase">
                        {item.mimeType?.split("/")[1] || "FILE"}
                      </span>
                    )}
                  </div>

                  {/* Info */}
                  <div className="p-1">
                    <p className="text-[10px] truncate leading-tight">{item.originalName}</p>
                    <p className="text-[10px] text-muted-foreground">
                      {(item.size / 1024).toFixed(0)} KB
                    </p>
                  </div>

                  {/* Action overlay */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-start justify-end p-1 gap-1 opacity-0 group-hover:opacity-100">
                    <button
                      className="rounded bg-background/80 p-0.5 hover:bg-background"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(item.url, "_blank");
                      }}
                      title="Görüntüle"
                    >
                      <Eye className="h-3.5 w-3.5" />
                    </button>
                    <button
                      className="rounded bg-background/80 p-0.5 hover:bg-destructive hover:text-white"
                      onClick={(e) => {
                        e.stopPropagation();
                        showDeleteConfirm({
                          onConfirm: () =>
                            deleteMutation.mutate(item.id, {
                              onSuccess: () => toast.success("Silindi"),
                              onError: (err: unknown) =>
                                toast.error(errorMessage(err, "Silinemedi")),
                            }),
                          title: "Medya Sil",
                          description: "Bu dosyayı silmek istediğinize emin misiniz?",
                        });
                      }}
                      title="Sil"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {mediaData && mediaData.totalPages > 1 && (
          <div className="flex items-center justify-center gap-3">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Önceki
            </Button>
            <span className="text-sm text-muted-foreground">
              {page} / {mediaData.totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page === mediaData.totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Sonraki
            </Button>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={handleClose}>
            İptal
          </Button>
          <Button disabled={!selectedUrl} onClick={handleSelect}>
            Seç
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
